/* eslint-disable no-console */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import stylesTheme from '@vr/lib/styles/theme';
import styled, { css } from 'styled-components';
import Spinner from '../../../elements/Spinner';
import axios from '../../../config/axios';
import Switch from '../../../elements/Switch';
import getEnv from '../../../helpers/getEnv';
import FormContent from './FormContent';

const Container = styled.div`
  margin-top: 20px;
  position: relative;

  ${({ isLoading }) =>
    isLoading &&
    css`
      & > div:not(.isLoading) {
        opacity: 0.5;
        pointer-events: none;
      }
    `}

  .isLoading {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const Subtitle = styled.h4`
  color: ${({ theme }) => theme.colors.white};
  font-size: 16px;
  margin: 50px 0 30px 0;
  font-weight: normal;
`;

const addressObj = {
  street: 'address.street',
  number: 'address.number',
  complement: 'address.complement',
  neighborhood: 'address.neighborhood',
  city: 'address.city',
  state: 'address.state',
  zipcode: 'address.zipcode',
};

const AddressForm = ({ address, formControl: { watch, ...formControl } }) => {
  const [withAddress, setWithAddress] = useState(!!address);
  const [loading, setLoading] = useState(false);
  const [disableFields, setDisableFields] = useState([
    addressObj.street,
    addressObj.number,
    addressObj.complement,
    addressObj.neighborhood,
    addressObj.city,
    addressObj.state,
  ]);
  const [reset, setReset] = useState(true);

  React.useEffect(() => {
    if (!withAddress) {
      setReset(false);
      setTimeout(() => {
        formControl.setValue(addressObj.zipcode, '', { shouldValidate: true });
      });

      formControl.unregister(addressObj.zipcode);
      formControl.unregister(addressObj.street);
      formControl.unregister(addressObj.number);
      formControl.unregister(addressObj.complement);
      formControl.unregister(addressObj.neighborhood);
      formControl.unregister(addressObj.city);
      formControl.unregister(addressObj.state);
    }
  }, [withAddress, formControl]);

  const searchAddress = React.useCallback(
    async cep => {
      setLoading(true);
      try {
        // 13315-000 (cep geral)
        const { data } = await axios.get(
          `${getEnv('usuario')}/zipcodes/${cep.replace(/\D/g, '')}`,
        );
        if (data.result) {
          formControl.setValue(addressObj.street, '', { shouldValidate: true });
          formControl.setValue(addressObj.state, '', { shouldValidate: true });
          formControl.setValue(addressObj.neighborhood, '', {
            shouldValidate: true,
          });
          formControl.setValue(addressObj.city, '', { shouldValidate: true });
          const obj = [];
          if (data.result.street) {
            formControl.setValue(addressObj.street, data.result.street, {
              shouldValidate: true,
            });
            formControl.trigger(addressObj.street);
            obj.push(addressObj.street);
          }
          if (data.result.state) {
            formControl.setValue(addressObj.state, data.result.state, {
              shouldValidate: true,
            });
            formControl.trigger(addressObj.state);
            obj.push(addressObj.state);
          }
          if (data.result.neighborhood) {
            formControl.setValue(
              addressObj.neighborhood,
              data.result.neighborhood,
              { shouldValidate: true },
            );
            formControl.trigger(addressObj.neighborhood);
            obj.push(addressObj.neighborhood);
          }
          if (data.result.city) {
            formControl.setValue(addressObj.city, data.result.city, {
              shouldValidate: true,
            });
            formControl.trigger(addressObj.city);
            obj.push(addressObj.city);
          }
          setDisableFields(obj);
        } else {
          throw new Error();
        }
      } catch (e) {
        console.log('Erro na busca de endereço', e);
        setDisableFields([]);
      }
      setLoading(false);
    },
    [formControl],
  );

  const onCepChange = React.useCallback(
    value => {
      if (value.length === 9) {
        setReset(false);
        searchAddress(value);
      }
      if (value.length < 9 && !reset) {
        setReset(true);
        setDisableFields([
          addressObj.street,
          addressObj.number,
          addressObj.complement,
          addressObj.neighborhood,
          addressObj.city,
          addressObj.state,
        ]);
        formControl.setValue(addressObj.street, '');
        formControl.setValue(addressObj.state, '');
        formControl.setValue(addressObj.neighborhood, '');
        formControl.setValue(addressObj.city, '');
        formControl.setValue(addressObj.number, '');
        formControl.setValue(addressObj.complement, '');
      }
    },
    [searchAddress, formControl, reset],
  );

  React.useEffect(() => {
    const value = formControl.getValues(addressObj.zipcode) || '';
    if (value.length === 9 || (value.length < 9 && !reset)) {
      onCepChange(value || '');
    }
    // eslint-disable-next-line
  }, [formControl.getValues(addressObj.zipcode)]);

  return (
    <Container isLoading={loading}>
      {loading && (
        <div className="isLoading">
          <Spinner />
        </div>
      )}
      <div>
        {address ? (
          <Subtitle>Endereço</Subtitle>
        ) : (
          <Switch
            label="Cadastrar meu endereço"
            name="cadastrar-endereco"
            style={{
              marginTop: stylesTheme.spacings.base * 4,
              marginBottom: stylesTheme.spacings.base * 2,
            }}
            checked={withAddress}
            onChange={val => setWithAddress(val)}
          />
        )}
      </div>
      {withAddress && (
        <FormContent
          formControl={formControl}
          watch={watch}
          disableFields={disableFields}
          address={address}
        />
      )}
    </Container>
  );
};

AddressForm.propTypes = {
  formControl: PropTypes.object.isRequired,
  address: PropTypes.object,
};

export default AddressForm;
